(ns web3.modals.forms
  (:require [web3.components.h-slider :as h-slider]
            [web3.i18n :as i]
            [web3.view :as v]
            [web3.routing.core :as routing]
            [web3.modals.core :as modal]
            [re-frame-simple.db :as db]
            [re-frame.db :refer [app-db]]
            [web3.forms.validation :as validation]
            [web3.persistence.schema :as schema]
            [reagent.core :as r]))

;; ========== Modal Forms ==========
;;
;; A breadcrumb-based modal (with back button) with 'local' state
;; which persists across multi-screen flows.

(defn state
  "Modal state, persists across multi-screen forms."
  []
  (db/get ::state))

(defn swap-state!
  [f & args]
  (swap! app-db update ::state
         (fn [state]
           (apply f state args))))

(defn loading?
  []
  (pos? (db/get ::loading-semaphore)))

(defn set-or-inc-loading!
  []
  (if (some? (db/get ::loading-semaphore))
    (db/update! ::loading-semaphore inc)
    (db/assoc! ::loading-semaphore 1)))

(defn dec-loading!
  []
  (if (some? (db/get ::loading-semaphore))
    (db/update! ::loading-semaphore dec)
    (db/assoc! 0)))

(defn clear-state!
  []
  (db/assoc! ::state nil))

(defn wrap-clear
  [this f]
  (fn [& args]
    (clear-state!)
    (apply f args)))

(defn merge-query
  "Allows for passing a map into the `:modal` query param to be merged into modal-state."
  [this]
  (some->> (routing/get-query :form-modal-data)
           (swap-state! update :query-data merge)))

(defn lightbox-header [{:keys [router
                               view
                               handler
                               route-params]}]
  (let [header-props (let [header-props (v/class-get view :header)]
                       (if (fn? header-props)
                         (header-props route-params)
                         header-props))
        back-href (if-let [prev-handler (some->> (get header-props :modal-sequence)
                                                 (take-while #(not= % handler))
                                                 (last))]
                    (routing/path-for (into [prev-handler] (apply concat (seq route-params))))
                    (some->> (routing/breadcrumbs router)
                             (drop-last)
                             (last)
                             :path
                             (routing/path-for router)))]
    (when-not (false? header-props)
      [modal/header
       (merge
        {:key "header"
         :title (when-not (:title header-props)
                  (i/get handler route-params))
         :back-href back-href}
        header-props)])))

(v/defview breadcrumb-slider
  "Container for 'add' screens which implements slide-in transitions, based on
   the handler's position in the breadcrumb."
  {:component-will-mount merge-query
   :component-will-receive-props merge-query
   :component-will-unmount clear-state!
   :component-did-update
   (fn [this]
     ((-> this
          (get-in [:props :router])
          routing/set-prev-breadcrumbs!)
      this))}
  [{:keys [router
           route-params
           handler
           view
           z-index
           on-close]
    :as props}]
  (let [index (or (v/class-get view :index)
                  (->>
                   ;; get breadcrumb (the longer of current or prev breadcrumb)
                   [(:breadcrumbs (v/prev))
                    (routing/breadcrumbs router)]
                   (sort-by count)
                   (last)
                   ;; count the # of preceeding views in breadcrumb
                   (take-while (comp (partial not= handler)
                                     :handler))
                   (count)))]
    [modal/lightbox
     (merge {:id (name router)
             :class "bg-off-white"
             :z-index z-index
             :on-close on-close}
            (v/class-get view :modal-props))
     (lightbox-header props)
     [h-slider/h-slider
      {:key "Slider"
       :index index
       :content/key handler
       :transition-heights true
       :content
       [view route-params]}]]))

(defn form-data []
  (::data (state)))

(defn get-field
  [key]
  (let [path (cond-> key
                     (not (vector? key)) (vector))]
    (get-in (state) (into [::data] path))))

(defn assoc-field! [key value]
  (let [path (cond-> key
                     (not (vector? key)) (vector))]
    (swap-state! assoc-in (into [::data] path) value)))

(defn field-props
  "Return props for a pro-flow field, given parent component state atom (used to track `:dirty?` status for the form) and key/path"
  [parent-state key]
  (let [path (cond-> key
                     (not (vector? key)) (vector))]
    {:change-value! #(swap-state! assoc-in (into [::data] path) %)
     :value (get-field path)
     :dirty? (:dirty? @parent-state)}))

(defn augment-validators [validators required-fields]
  (reduce (fn [m k]
            (update m k (comp distinct (fnil conj [])) [:required])) validators required-fields))

(defn form-submit
  [{this :component
    :keys [submit!
           validators
           required]} e]
  (some-> e (.preventDefault))
  (let [validators (augment-validators validators required) {:keys [::data]} (state)
        validations (->> validators
                         (schema/validate-paths (into (keys data) (keys validators))
                                                data))]
    (if (validation/invalid? validations)
      (swap! (:state this) assoc :dirty? true)
      (submit! data))))